import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '../components/Icons';
import { getExploreFeed, ExploreImage } from '../lib/exploreApi';

// ── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 15;

const CATEGORIES: string[] = [
  '', 'interior-design', 'bedroom', 'tv-unit', 'home-decor', 'living-room',
];

const CATEGORY_LABELS: Record<string, string> = {
  '':               'All',
  'interior-design': 'Interior',
  'bedroom':        'Bedroom',
  'tv-unit':        'TV Unit',
  'home-decor':     'Decor',
  'living-room':    'Living Room',
};

// ── Component ────────────────────────────────────────────────────────────────

function Explore() {
  const navigate = useNavigate();

  const [items,       setItems]       = useState<ExploreImage[]>([]);
  const [nextCursor,  setNextCursor]  = useState<string | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState('');
  const [category,    setCategory]    = useState('');
  const [lightbox,    setLightbox]    = useState<ExploreImage | null>(null);

  const sentinelRef   = useRef<HTMLDivElement>(null);
  const fetchGenRef   = useRef(0);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const fetchPage = useCallback(async (cursor?: string) => {
    const isMore = !!cursor;
    const gen = ++fetchGenRef.current;           // bump generation for this request
    if (isMore) setLoadingMore(true); else setLoading(true);
    setError('');
    try {
      const result = await getExploreFeed({
        limit:    PAGE_SIZE,
        cursor,
        category: category || undefined,
      });
      if (fetchGenRef.current !== gen) return;   // stale response — discard
      if (isMore) setItems(prev => [...prev, ...result.items]);
      else        setItems(result.items);
      setNextCursor(result.nextCursor);
    } catch {
      if (fetchGenRef.current !== gen) return;   // stale error — discard
      setError('Could not load images. Please try again.');
    } finally {
      if (fetchGenRef.current !== gen) return;   // stale finally — discard
      if (isMore) setLoadingMore(false); else setLoading(false);
    }
  }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial load + refetch when category changes
  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // IntersectionObserver — auto-load next page when sentinel enters viewport
  useEffect(() => {
    if (!nextCursor || loadingMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) fetchPage(nextCursor); },
      { rootMargin: '300px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [nextCursor, loadingMore, fetchPage]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCategoryChange = (cat: string) => {
    if (cat === category) return;
    setItems([]);
    setNextCursor(null);
    setCategory(cat);
  };

  const handleUseAsInspiration = () => {
    if (!lightbox) return;
    navigate('/new-project', { state: { inspirationUrl: lightbox.imageUrl } });
  };

  // ── Broken image fallback ─────────────────────────────────────────────────

  const hideBrokenImage = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const card = (e.currentTarget as HTMLElement).closest('.explore-card') as HTMLElement | null;
    if (card) card.style.display = 'none';
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="page-root">

      {/* Header */}
      <header className="app-header">
        <div className="header-inner">
          <button className="header-action-btn" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowLeft size={20} />
          </button>
          <h1 className="header-title">Explore</h1>
          <div style={{ width: 36 }} />
        </div>
      </header>

      <div className="page-scroll">

        {/* Category filter chips */}
        <div className="explore-chips" role="tablist" aria-label="Filter by category">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              role="tab"
              aria-selected={cat === category}
              disabled={loading || loadingMore}
              className={`explore-chip${cat === category ? ' explore-chip-active' : ''}`}
              onClick={() => handleCategoryChange(cat)}
            >
              {CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="explore-loading">
            <div className="spinner" />
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="explore-error">
            <p>{error}</p>
            <button className="btn btn-secondary btn-sm" onClick={() => fetchPage()}>
              Retry
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && items.length === 0 && (
          <div className="empty-state">
            <p>No images found.</p>
          </div>
        )}

        {/* Masonry grid */}
        {items.length > 0 && (
          <div className="explore-grid" role="list">
            {items.map(img => (
              <button
                key={img.id}
                role="listitem"
                className="explore-card"
                onClick={() => setLightbox(img)}
                aria-label={img.title ?? 'Inspiration image'}
              >
                <img
                  src={img.imageUrl}
                  alt={img.title ?? ''}
                  loading="lazy"
                  onError={hideBrokenImage}
                  {...(img.width && img.height
                    ? { style: { aspectRatio: `${img.width}/${img.height}` } }
                    : {})}
                />
              </button>
            ))}
          </div>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />

        {/* Manual load-more fallback (also shown while loading next page) */}
        {nextCursor && (
          <div className="explore-load-more">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => fetchPage(nextCursor)}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}

      </div>{/* end page-scroll */}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="explore-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Image detail"
          onClick={e => { if (e.target === e.currentTarget) setLightbox(null); }}
        >
          <div className="explore-lightbox-inner">
            <button
              className="explore-lightbox-close"
              onClick={() => setLightbox(null)}
              aria-label="Close"
            >
              ✕
            </button>
            <img
              src={lightbox.imageUrl}
              alt={lightbox.title ?? ''}
              className="explore-lightbox-img"
            />
            {lightbox.title && (
              <p className="explore-lightbox-title">{lightbox.title}</p>
            )}
            <div className="explore-lightbox-actions">
              <button className="btn btn-primary" onClick={handleUseAsInspiration}>
                Use as inspiration
              </button>
              {lightbox.sourceUrl && (
                <a
                  href={lightbox.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost btn-sm"
                >
                  View on Pinterest
                </a>
              )}
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

export default Explore;
